import React from 'react';
import PricingDetails from '../pricing-details/pricing-details';
import shapeDesktop from './assets/subheader-shape-desktop.svg?url';
import shapeMobile from './assets/subheader-shape-mobile.svg?url';
import shapeTablet from './assets/subheader-shape-tablet.svg?url';

interface SubHeaderBandProps {
  className?: string;
  children?: React.ReactNode;
  /**
   * Render the vehicle name / rate inside the band. Disable it to use the band
   * as a purely decorative strip (e.g. the multiple applications overview).
   * @default true
   */
  showPricing?: boolean;
}

/*
 * Vehicle sub-header band (Figma: MyCar - Car Overview, Rectangle 80/1297 +
 * Mask group + vehicle/rate texts). Mobile-first, project breakpoints:
 *
 *            band height  content box                       decoration
 *   mobile   92px         16px margins, 16px top            219x168, 8px right / 8px up
 *   tablet   116px        32px margins, 24px top            309x237, 16px right / 48px up
 *   desktop  120px        784px centred, 24px top           283x216, 11px right / 48px up
 *
 * The decoration starts behind the header bar, the band clips it. Partners can
 * re-theme through `--subheader-background` / `--subheader-text` or hide the
 * Ayvens shape with `.subheader-decoration { display: none }` in their theme.css.
 */
const decorationClass =
  'subheader-decoration pointer-events-none absolute m-0 block max-w-none select-none';

const SubHeaderBand: React.FC<SubHeaderBandProps> = ({
  className = '',
  children,
  showPricing = true,
}) => (
  <section
    className={`subheader-band relative overflow-hidden bg-(--subheader-background,var(--vel-color-brand-support2-400,#73d2d2)) ${className}`.trim()}
    aria-label='Dein Fahrzeug'
    data-testid='subheader'
  >
    <div className='relative mx-auto w-full px-4 pt-4 pb-6 md:px-8 md:pt-6 md:pb-5 lg:max-w-[784px] lg:px-0'>
      <img
        src={shapeMobile}
        alt=''
        aria-hidden='true'
        width={219}
        height={168}
        className={`${decorationClass} -top-2 right-2 h-[168px] w-[219px] md:hidden`}
      />
      <img
        src={shapeTablet}
        alt=''
        aria-hidden='true'
        width={309}
        height={237}
        className={`${decorationClass} -top-12 right-4 h-[237px] w-[309px] max-md:hidden lg:hidden`}
      />
      <img
        src={shapeDesktop}
        alt=''
        aria-hidden='true'
        width={282.833}
        height={216.178}
        className={`${decorationClass} -top-12 -right-[11px] h-[216.178px] w-[282.833px] max-lg:hidden`}
      />
      {showPricing && <PricingDetails className='relative' />}
      {children}
    </div>
  </section>
);

export default SubHeaderBand;
